


import React, { useState, useEffect, useCallback } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { 
  Eye, Pencil, Trash2, Plus, Search, RotateCcw,
  Loader2, ArrowLeft, CheckCircle, IndianRupee, 
  Calendar, X, Hash, User, CreditCard, Info,
  Download, FileText, FileSpreadsheet 
} from "lucide-react";

// Updated Imports to fix the "doc.autoTable is not a function" error
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable"; 
import * as XLSX from "xlsx";

const Receipts = () => {
  // --- VIEW STATE ---
  const [view, setView] = useState("list"); 
  
  // --- DATA STATES ---
  const [receiptsList, setReceiptsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false); 
  const [inputValue, setInputValue] = useState(""); 
  const [searchTerm, setSearchTerm] = useState(""); 
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  // --- MEMBER SEARCH DROPDOWN STATES ---
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [memberSuggestions, setMemberSuggestions] = useState([]);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [selectedMemberName, setSelectedMemberName] = useState("");

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(null);
  const [saving, setSaving] = useState(false);

  const initialFormState = {
    receiptId: null,
    memberId: "",
    receiptNo: "",
    amount: "",
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: "Cash",
    comments: "",
    status: "SUCCESS"
  };
  
  const [formData, setFormData] = useState(initialFormState);

  // --- HELPERS ---
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "---";
    const date = new Date(dateStr);
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  };

  // --- FETCH ALL FOR EXPORT ---
  const fetchAllForExport = async () => {
    setExporting(true);
    try {
      const url = `${APIConfig.APIACTIVEURL}${APIConfig.RECEIPT_GET_ALL}?pageNumber=1&pageSize=5000&searchTerm=${searchTerm}`;
      const response = await fetch(url);
      const result = await response.json();
      setExporting(false);
      return result.data || [];
    } catch (e) {
      setExporting(false);
      Swal.fire("Error", "Failed to fetch data for export", "error");
      return [];
    }
  };

  // --- EXPORT TO EXCEL ---
  const handleExportExcel = async () => {
    const allData = await fetchAllForExport();
    if (allData.length === 0) return Swal.fire("No Data", "No records found to export", "info");

    const worksheet = XLSX.utils.json_to_sheet(allData.map(r => ({
      "Receipt No": r.receiptNo,
      "Member Name": r.member?.memberName,
      "Phone": r.member?.phoneNo,
      "Amount": r.amount,
      "Date": formatDisplayDate(r.paymentDate),
      "Mode": r.paymentMode,
      "Status": r.status,
      "Comments": r.comments
    })));
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Receipts");
    XLSX.writeFile(workbook, `Receipt_Report_${searchTerm || 'Full'}.xlsx`);
  };

  // --- EXPORT LIST TO PDF (FIXED LOGIC) ---
  const handleExportPDF = async () => {
    const allData = await fetchAllForExport();
    if (allData.length === 0) return Swal.fire("No Data", "No records found to export", "info");

    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Receipt Transaction Report", 14, 22);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleString()} | Name: ${searchTerm || 'All Records'}`, 14, 30);

    const tableColumn = ["Receipt No", "Member Name", "Date", "Mode", "Amount", "Status"];
    const tableRows = allData.map(r => [
      r.receiptNo,
      r.member?.memberName,
      formatDisplayDate(r.paymentDate),
      r.paymentMode,
      `Rs. ${r.amount.toLocaleString()}`,
      r.status
    ]);

    // FIXED: Calling autoTable as a function instead of doc.autoTable
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 35,
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229] },
      styles: { fontSize: 9 }
    });

    doc.save(`Receipt_Report_${searchTerm || 'Full'}.pdf`);
  };

  // --- DOWNLOAD INDIVIDUAL RECEIPT (A4) ---
  const downloadIndividualReceipt = (r) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    
    // Border
    doc.setDrawColor(79, 70, 229);
    doc.setLineWidth(0.5);
    doc.rect(10, 10, 190, 277);

    // Header Area
    doc.setFillColor(79, 70, 229);
    doc.rect(10, 10, 190, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("AMOUNT RECEIPT", 105, 30, { align: "center" });

    // Content Area
    doc.setTextColor(27, 37, 89);
    doc.setFontSize(12);
    
    doc.text(`Receipt No: ${r.receiptNo}`, 20, 55);
    doc.text(`Date: ${formatDisplayDate(r.paymentDate)}`, 140, 55);

    doc.setDrawColor(200, 200, 200);
    doc.line(20, 60, 190, 60);

    // Member Info
    doc.setFontSize(10);
    doc.setTextColor(163, 174, 208);
    doc.text("RECEIVED FROM:", 20, 75);
    doc.setFontSize(14);
    doc.setTextColor(27, 37, 89);
    doc.text(`${r.member?.memberName}`, 20, 83);
    doc.setFontSize(11);
    doc.text(`Contact: ${r.member?.phoneNo || 'N/A'}`, 20, 90);

    // Payment Box
    doc.setFillColor(244, 247, 254);
    doc.rect(20, 105, 170, 40, 'F');
    
    doc.setFontSize(12);
    doc.text("Payment Particulars", 25, 115);
    doc.setFontSize(10);
    doc.text(`Method: ${r.paymentMode}`, 25, 125);
    doc.text(`Status: ${r.status}`, 25, 132);
    doc.text(`Remarks: ${r.comments || 'No specific remarks'}`, 25, 139);

    // Amount Area
    doc.setFontSize(16);
    doc.text("Total Amount Received:", 20, 165);
    doc.setFontSize(28);
    doc.setTextColor(5, 150, 105); 
    doc.text(`Rs. ${r.amount.toLocaleString()}/-`, 20, 180);

    // Footer
    doc.setFontSize(10);
    doc.setTextColor(163, 174, 208);
    doc.text("This is a computer generated receipt and does not require a physical signature.", 105, 260, { align: "center" });
    doc.text("Thank you for your business!", 105, 265, { align: "center" });

    doc.save(`Receipt_${r.receiptNo}.pdf`);
  };

  // --- SEARCH MEMBERS ---
  const searchMembersFromDB = async (query) => {
    if (query.length < 1) {
        setMemberSuggestions([]);
        return;
    }
    try {
        const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_GET_ALL}?pageNumber=1&pageSize=10&searchTerm=${query}`);
        const data = await res.json();
        setMemberSuggestions(data.data || []);
    } catch (e) { console.error(e); }
  };

  // --- FETCH PAGINATED LIST ---
  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${APIConfig.APIACTIVEURL}${APIConfig.RECEIPT_GET_ALL}?pageNumber=${currentPage}&pageSize=${pageSize}&searchTerm=${searchTerm}`;
      const response = await fetch(url);
      const result = await response.json();
      setReceiptsList(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  // --- HANDLERS ---
  const handleSearchClick = () => { setSearchTerm(inputValue); setCurrentPage(1); };
  const handleClearSearch = () => { setInputValue(""); setSearchTerm(""); setCurrentPage(1); };

  const openForm = (receipt = null) => {
    if (receipt) {
        setFormData({ ...receipt, paymentDate: receipt.paymentDate?.split('T')[0] });
        setSelectedMemberName(`${receipt.member?.memberName || ""} (${receipt.member?.phoneNo || ""})`);
    } else {
        setFormData(initialFormState);
        setSelectedMemberName("");
        setMemberSearchQuery("");
    }
    setView("form");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const selectMember = (m) => {
    setFormData({ ...formData, memberId: m.memberId });
    setSelectedMemberName(`${m.memberName} (${m.phoneNo || "No Number"})`);
    setShowMemberDropdown(false);
    setMemberSearchQuery("");
  };

 const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.memberId) return Swal.fire("Required", "Please select a member.", "warning");
    
    setSaving(true);
    try {
    const payload = {
        receiptId: formData.receiptId,
        memberId: formData.memberId,
        receiptNo: formData.receiptNo,
        amount: parseFloat(formData.amount), 
        paymentDate: formData.paymentDate,
        paymentMode: formData.paymentMode,   
        comments: formData.comments,         
        status: "SUCCESS"                    
      };

    const res = await fetch(APIConfig.APIACTIVEURL + APIConfig.RECEIPT_BASE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (res.ok && result.success) {
        setView("list");
        fetchReceipts();
        Swal.fire({ icon: 'success', title: 'Receipt Saved', text: 'Member balance updated!', timer: 1500, showConfirmButton: false });
      } else {
        Swal.fire('Error', result.message || "Failed to save", 'error');
      }
    } catch (err) { Swal.fire('Error', 'Server connection failed', 'error'); }
    setSaving(false);
};

  const handleDelete = async (id) => {
    const result = await Swal.fire({ title: 'Delete Receipt?', text: 'This action cannot be undone.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#ef4444' });
    if (result.isConfirmed) {
      await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.RECEIPT_BASE}/${id}`, { method: "DELETE" });
      fetchReceipts();
    }
  };

  const styles = {
    container: { padding: "40px 60px", backgroundColor: "#f4f7fe", minHeight: "calc(100vh - 70px)", fontFamily: "'Inter', sans-serif" },
    card: { backgroundColor: "#fff", borderRadius: "24px", boxShadow: "0 10px 30px rgba(0,0,0,0.02)", overflow: "hidden", border: '1px solid #f1f5f9' },
    label: { display: "block", fontSize: "11px", fontWeight: "800", color: "#a3aed0", marginBottom: "8px", textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { padding: "14px", borderRadius: "12px", border: "1.5px solid #e2e8f0", outline: "none", fontSize: "15px", width: '100%', boxSizing: 'border-box' },
    btnPrimary: { backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "14px 28px", borderRadius: "14px", cursor: "pointer", fontWeight: "700", display: "flex", alignItems: "center", gap: "10px" },
   btnReset: { backgroundColor: "#fff", color: "#64748b", border: "1.5px solid #e2e8f0", padding: "14px 25px", borderRadius: "14px", cursor: "pointer", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px" },
    btnDownload: (bg) => ({ backgroundColor: bg, color: "#fff", border: "none", padding: "14px 20px", borderRadius: "14px", cursor: "pointer", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px" }),
    statusBadge: (status) => ({
        fontSize: '10px', fontWeight: '900', padding: '5px 12px', borderRadius: '15px',
        background: status === 'SUCCESS' ? '#dcfce7' : '#fee2e2',
        color: status === 'SUCCESS' ? '#059669' : '#ef4444'
    }),
    suggestionItem: { padding: '12px 15px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '14px', transition: '0.2s' }
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        
        {view === "list" && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '35px' }}>
              <div>
                <h1 style={{ fontSize: '30px', fontWeight: '900', color: '#1b2559', margin: 0 }}>Receipts</h1>
              </div>
              <button style={styles.btnPrimary} onClick={() => openForm()}>
                <Plus size={20} /> Create Receipt
              </button>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '30px', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '15px', top: '15px', color: '#cbd5e1' }} />
                    <input style={{ ...styles.input, paddingLeft: '45px' }} placeholder="Search Receipt No or Member Name..." value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()} />
                </div>
                <button style={styles.btnPrimary} onClick={handleSearchClick}>Search</button>
                <button onClick={handleClearSearch} style={styles.btnReset}><RotateCcw size={18}/> Clear</button>
                
                <div style={{ flex: 1 }}></div>

                <button 
                  disabled={exporting} 
                  onClick={handleExportPDF} 
                  style={styles.btnDownload('#8b5cf6')} 
                >
                  {exporting ? <Loader2 size={18} className="animate-spin"/> : <FileText size={18}/>} PDF Report
                </button>
                <button 
                  disabled={exporting} 
                  onClick={handleExportExcel} 
                  style={styles.btnDownload('#059669')} 
                >
                  {exporting ? <Loader2 size={18} className="animate-spin"/> : <FileSpreadsheet size={18}/>} Excel Report
                </button>
            </div>

            <div style={styles.card}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid #f8fafc', background: '#fcfcfd' }}>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0' }}>RECEIPT DETAILS</th>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0' }}>MEMBER</th>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0' }}>AMOUNT</th>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0' }}>DATE</th>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0', textAlign: 'center' }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" style={{ textAlign:'center', padding:'80px' }}><Loader2 className="animate-spin" color="#4f46e5" /></td></tr>
                  ) : receiptsList.length === 0 ? (
                    <tr><td colSpan="5" style={{ textAlign:'center', padding:'80px', color:'#a3aed0', fontWeight:'600' }}>No matching records found.</td></tr>
                  ) : receiptsList.map(r => (
                    <tr key={r.receiptId} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '20px 24px' }}>
                        <div style={{ fontWeight: '800', color: '#1b2559' }}>{r.receiptNo || "NO-NO"}</div>
                        <div style={{ fontSize: '11px', color: '#4f46e5', fontWeight: '700' }}>{r.paymentMode}</div>
                      </td>
                      <td style={{ padding: '20px 24px' }}>
                        <div style={{ fontWeight: '700', color: '#1b2559' }}>{r.member?.memberName}</div>
                        <div style={{ fontSize: '12px', color: '#a3aed0' }}>{r.member?.phoneNo}</div>
                      </td>
                      <td style={{ padding: '20px 24px' }}>
                        <div style={{ fontWeight: '900', color: '#059669', fontSize: '16px' }}>₹{r.amount.toLocaleString()}</div>
                        <span style={styles.statusBadge(r.status)}>{r.status}</span>
                      </td>
                      <td style={{ padding: '20px 24px', fontWeight: '700', color: '#1b2559' }}>{formatDisplayDate(r.paymentDate)}</td>
                      <td style={{ padding: '20px 24px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button title="Download PDF" onClick={() => downloadIndividualReceipt(r)} style={{ border: 'none', background: '#f5f3ff', color: '#8b5cf6', padding: '10px', borderRadius: '10px', cursor: 'pointer' }}><Download size={16}/></button>
                            <button title="View" onClick={() => {setViewReceipt(r); setShowViewModal(true)}} style={{ border: 'none', background: '#f4f7fe', color: '#1b2559', padding: '10px', borderRadius: '10px', cursor: 'pointer' }}><Eye size={16}/></button>
                            <button title="Edit" onClick={() => openForm(r)} style={{ border: 'none', background: '#eef2ff', color: '#4f46e5', padding: '10px', borderRadius: '10px', cursor: 'pointer' }}><Pencil size={16}/></button>
                            <button title="Delete" onClick={() => handleDelete(r.receiptId)} style={{ border: 'none', background: '#fff1f2', color: '#ef4444', padding: '10px', borderRadius: '10px', cursor: 'pointer' }}><Trash2 size={16}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fcfcfd' }}>
                <span style={{ color: '#a3aed0', fontSize: '14px', fontWeight: '700' }}>Showing Page {currentPage} of {totalPages}</span>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button disabled={currentPage === 1} onClick={() => setCurrentPage(c => c - 1)} style={styles.btnReset}><ArrowLeft size={22}/> Prev</button>
                  <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(c => c + 1)} style={styles.btnReset}>Next <ArrowLeft size={22} style={{transform: 'rotate(180deg)'}}/></button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* --- FORM VIEW --- */}
        {view === "form" && (
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                <button onClick={() => setView("list")} style={{ border: 'none', background: 'none', color: '#a3aed0', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '25px' }}>
                    <ArrowLeft size={20}/> Back to List
                </button>

                <div style={{ background: '#fff', borderRadius: '32px', padding: '40px', border: '1px solid #f1f5f9' }}>
                    <h2 style={{ fontSize: '24px', fontWeight: '900', color: '#1b2559', marginBottom: '30px' }}>{formData.receiptId ? "Update Receipt" : "Generate New Receipt"}</h2>
                    
                    <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
                        <div style={{ gridColumn: 'span 2', position: 'relative' }}>
                            <label style={styles.label}>Member Lookup *</label>
                            {selectedMemberName ? (
                                <div style={{ ...styles.input, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f0fdf4', borderColor: '#bcf0da' }}>
                                    <span style={{fontWeight:'700', color:'#059669'}}><User size={16} style={{marginRight:8}} /> {selectedMemberName}</span>
                                    <X size={18} style={{cursor:'pointer'}} onClick={() => {setSelectedMemberName(""); setFormData({...formData, memberId:""})}} />
                                </div>
                            ) : (
                                <>
                                    <div style={{ position: 'relative' }}>
                                        <Search size={18} style={{ position: 'absolute', left: '15px', top: '15px', color: '#cbd5e1' }} />
                                        <input style={{ ...styles.input, paddingLeft: '45px' }} placeholder="Type Member Name or Mobile to search..." value={memberSearchQuery} onChange={(e) => {setMemberSearchQuery(e.target.value); searchMembersFromDB(e.target.value); setShowMemberDropdown(true)}} />
                                    </div>
                                    {showMemberDropdown && memberSuggestions.length > 0 && (
                                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', zIndex: 10, borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0', marginTop: '5px', maxHeight: '200px', overflowY: 'auto' }}>
                                            {memberSuggestions.map(m => (
                                                <div key={m.memberId} style={styles.suggestionItem} onClick={() => selectMember(m)}>
                                                    <div style={{fontWeight:'700', color:'#1b2559'}}>{m.memberName}</div>
                                                    <div style={{fontSize:'12px', color:'#a3aed0'}}>{m.phoneNo}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div><label style={styles.label}>Receipt Number</label><div style={{position:'relative'}}><Hash size={18} style={{position:'absolute', left:15, top:15, color:'#cbd5e1'}} /><input name="receiptNo" style={{...styles.input, paddingLeft:45}} value={formData.receiptNo} onChange={handleInputChange} placeholder="Ex: RCP-100" /></div></div>
                        <div><label style={styles.label}>Amount (INR) *</label><div style={{position:'relative'}}><IndianRupee size={18} style={{position:'absolute', left:15, top:15, color:'#cbd5e1'}} /><input type="number" name="amount" style={{...styles.input, paddingLeft:45, fontWeight:'800', color:'#059669'}} value={formData.amount} onChange={handleInputChange} /></div></div>
                        <div><label style={styles.label}>Payment Date</label><div style={{position:'relative'}}><Calendar size={18} style={{position:'absolute', left:15, top:15, color:'#cbd5e1'}} /><input type="date" name="paymentDate" style={{...styles.input, paddingLeft:45}} value={formData.paymentDate} onChange={handleInputChange} /></div></div>
                        <div><label style={styles.label}>Payment Mode</label><div style={{position:'relative'}}><CreditCard size={18} style={{position:'absolute', left:15, top:15, color:'#cbd5e1'}} />
                            <select name="paymentMode" style={{...styles.input, paddingLeft:45}} value={formData.paymentMode} onChange={handleInputChange}>
                                <option value="Cash">Cash</option><option value="Bank Transfer">Bank Transfer</option><option value="UPI">UPI</option><option value="Cheque">Cheque</option><option value="Other">Other</option>
                            </select></div>
                        </div>
                        <div><label style={styles.label}>Transaction Status</label>
                            <select name="status" style={styles.input} value={formData.status} onChange={handleInputChange}>
                                <option value="SUCCESS">SUCCESS</option><option value="PENDING">PENDING</option><option value="FAILED">FAILED</option>
                            </select>
                        </div>
                        <div style={{ gridColumn: 'span 2' }}><label style={styles.label}>Comments / Internal Notes</label><textarea name="comments" style={{ ...styles.input, height: '80px', resize: 'none' }} value={formData.comments} onChange={handleInputChange} /></div>

                        <div style={{ gridColumn: 'span 2', display: 'flex', gap: '15px', marginTop: '20px' }}>
                            <button type="submit" disabled={saving} style={{ ...styles.btnPrimary, flex: 1, justifyContent: 'center', height: '55px' }}>
                                {saving ? <Loader2 className="animate-spin" /> : <CheckCircle />} {formData.receiptId ? "Update Receipt" : "Confirm & Save Receipt"}
                            </button>
                            <button type="button" onClick={() => setView("list")} style={{ ...styles.btnReset, padding: '0 40px' }}>Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
        )}

        {/* --- VIEW MODAL --- */}
        {showViewModal && viewReceipt && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 5000 }}>
                <div style={{ backgroundColor: '#fff', padding: '40px', borderRadius: '32px', width: '600px', boxShadow: '0 25px 50px rgba(0,0,0,0.2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                        <h2 style={{ margin: 0, fontWeight: '900', color: '#1b2559' }}>Receipt Details</h2>
                        <X size={24} style={{ cursor: 'pointer', color: '#cbd5e1' }} onClick={() => setShowViewModal(false)} />
                    </div>
                    
                    <div style={{ display: 'grid', gap: '20px' }}>
                        <div style={{ padding: '25px', background: 'linear-gradient(135deg, #f4f7fe 0%, #fff 100%)', borderRadius: '20px', border: '1px solid #eef2ff', textAlign: 'center' }}>
                            <div style={{ color: '#a3aed0', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase' }}>Transaction Amount</div>
                            <div style={{ fontSize: '32px', fontWeight: '900', color: '#059669' }}>₹{viewReceipt.amount.toLocaleString()}</div>
                            <span style={styles.statusBadge(viewReceipt.status)}>{viewReceipt.status}</span>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div><label style={styles.label}>Receipt No</label><div style={{fontWeight:'700', color:'#1b2559'}}>{viewReceipt.receiptNo}</div></div>
                            <div><label style={styles.label}>Payment Date</label><div style={{fontWeight:'700', color:'#1b2559'}}>{formatDisplayDate(viewReceipt.paymentDate)}</div></div>
                            <div><label style={styles.label}>Member Name</label><div style={{fontWeight:'700', color:'#1b2559'}}>{viewReceipt.member?.memberName}</div></div>
                            <div><label style={styles.label}>Payment Mode</label><div style={{fontWeight:'700', color:'#4f46e5'}}>{viewReceipt.paymentMode}</div></div>
                        </div>

                        <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '15px' }}>
                            <label style={styles.label}><Info size={12} style={{marginRight:5}}/> Comments</label>
                            <div style={{ fontSize: '14px', color: '#64748b', fontStyle: 'italic' }}>{viewReceipt.comments || "No notes provided for this transaction."}</div>
                        </div>
                    </div>
                    <div style={{display:'flex', gap:'12px', marginTop:'30px'}}>
                         <button onClick={() => downloadIndividualReceipt(viewReceipt)} style={{ ...styles.btnDownload('#4f46e5'), flex: 1, justifyContent:'center' }}>Download PDF</button>
                         <button onClick={() => setShowViewModal(false)} style={{ ...styles.btnReset, flex: 1, justifyContent:'center' }}>Close</button>
                    </div>
                </div>
            </div>
        )}

      </div>
    </>
  );
};

export default Receipts;